import { useQueryState } from 'hooks/useQueryState';
import { usePipelines } from '@/state/pipelines/usePipelines';
import { useTourScreen } from '@/tours';
import { PipelineList } from './PipelineList';
import { PipelineBoard } from './PipelineBoard';

/**
 * The pipelines page: an index, and one board at a time.
 *
 * Which board is open lives in the query string rather than in a route param, so the
 * back button works, a board can be linked to, and the menu entry stays a single URL.
 */
export const PipelinesScreen = () => {
  const [pipelineId, setPipelineId] = useQueryState<number>('pipeline', 0);
  // Owned here rather than in PipelineList so this screen knows when the index is
  // empty: the first-run explainer opens with its own heading, and the sibling
  // empty-state screens (surveys, implementation) carry no page header either.
  const manager = usePipelines();
  // An open board is its own tour: the index and a board share this one route.
  useTourScreen(pipelineId ? 'board' : null);

  const isFirstRun = !pipelineId && !!manager.websiteId && !manager.isLoading && manager.pipelines.length === 0;

  return (
    <main className="flex flex-col gap-[14px]">
      {!isFirstRun && (
        <div className="space-y-1">
          <h1 className="text-title font-semibold tracking-tight">Pipelines</h1>
          <p className="supporting max-w-3xl">
            Decide the order and combination experiments run in. Stages run one after another; everything inside a stage
            runs at the same time.
          </p>
        </div>
      )}

      {pipelineId ? (
        <PipelineBoard pipelineId={pipelineId} onBack={() => setPipelineId(0)} />
      ) : (
        <PipelineList manager={manager} onOpen={setPipelineId} />
      )}
    </main>
  );
};
